import { daysBetweenDateOnly, daysUntilDateOnly } from '@/lib/date';
import type { SprintItem } from '@hygge/shared/types';

/**
 * Whether a sprint is going to land, said in numbers.
 *
 * This lives apart from the UI because it is the one figure on the Boards page
 * a client will argue with. "Behind pace" is a judgement, and a judgement needs
 * a rule that is written down once, testable, and quotable on screen — not a
 * comparison inlined into a row component where it can drift from the tooltip
 * that explains it.
 *
 * The rule: a sprint is behind when the share of its points that is done sits
 * more than ten percentage points below the share of its window that has gone.
 * Nothing is judged in the first quarter of the window, because a sprint that
 * has burned two days of fourteen and finished nothing is not in trouble, it
 * has just started.
 */

/** How far done may trail elapsed before it counts as behind. */
export const BEHIND_GAP_PCT = 10;
/** No judgement before this much of the window has gone — day-one noise is not risk. */
export const GRACE_ELAPSED_PCT = 25;

export interface SprintPace {
  /** Scope as it stands today. */
  current: number;
  /** Scope at kickoff, when a snapshot was taken. */
  committed: number | null;
  completed: number;
  /** Points added (or removed) since kickoff. Null when there is no snapshot. */
  scopeDelta: number | null;
  /** 0–100, clamped. Null when there are no estimates to measure against. */
  donePct: number | null;
  /** 0–100, clamped. Null when the window is unknown. */
  elapsedPct: number | null;
  dayOf: number | null;
  windowDays: number | null;
  daysLeft: number | null;
  isOverrun: boolean;
  isBehind: boolean;
  /** The single condition the page reports. */
  isAtRisk: boolean;
}

const clamp = (n: number) => Math.min(100, Math.max(0, n));

export function sprintPace(sprint: SprintItem): SprintPace {
  const current = sprint.totalPoints ?? 0;
  const committed = sprint.startSnapshot?.totalPoints ?? null;
  const completed = sprint.completedPoints ?? 0;

  // Clamped, so a re-estimate that shrinks scope below what is already finished
  // cannot print "112% done" — a bar cannot be more than full, and a number the
  // reader knows is impossible discredits every other number beside it.
  const donePct = current > 0 ? clamp((completed / current) * 100) : null;

  const windowDays =
    sprint.durationDays ??
    (sprint.startDate && sprint.endDate
      ? // Inclusive of both ends: a Monday-to-Friday sprint is five days, not four.
        (daysBetweenDateOnly(sprint.startDate, sprint.endDate) ?? 0) + 1
      : null);

  // Day 1 is the start date itself. `daysUntilDateOnly` is negative in the past,
  // which is what makes this arithmetic rather than a branch.
  const daysSinceStart = sprint.startDate ? daysUntilDateOnly(sprint.startDate) : null;
  const dayOf = daysSinceStart === null ? null : -daysSinceStart + 1;

  const elapsedPct =
    windowDays !== null && windowDays > 0 && dayOf !== null
      ? clamp((dayOf / windowDays) * 100)
      : null;

  const daysLeft = sprint.endDate ? daysUntilDateOnly(sprint.endDate) : null;
  const isOverrun = daysLeft !== null && daysLeft < 0;

  const isBehind =
    !isOverrun &&
    donePct !== null &&
    elapsedPct !== null &&
    elapsedPct >= GRACE_ELAPSED_PCT &&
    donePct + BEHIND_GAP_PCT < elapsedPct;

  return {
    current,
    committed,
    completed,
    scopeDelta: committed === null ? null : current - committed,
    donePct,
    elapsedPct,
    dayOf,
    windowDays,
    daysLeft,
    isOverrun,
    isBehind,
    isAtRisk: isBehind || isOverrun,
  };
}

/**
 * The pace rule, said as one sentence.
 *
 * The board Overview leads with this line, which makes it the sentence a client
 * reads out in a status meeting — so it is generated from the same `sprintPace`
 * the rest of the page uses, never from a second comparison written inline. Two
 * rules that can disagree is one rule too many.
 *
 * Three of the six answers are refusals, and that is the point. `isBehind` is
 * false inside the grace window, false with no estimates and false with no
 * dates; a sentence keyed off that flag alone would print "on pace" in all
 * three, which is a verdict nobody measured. Each refusal instead names what is
 * missing. Nothing here is ever predicted: there is no completed sprint on this
 * product to derive a velocity from, so a ship date would be invented.
 */
export interface PaceVerdict {
  /** The sprint's name, emphasised on screen. Null when there is no sprint to name. */
  subject: string | null;
  /** The rest of the sentence, ending in a full stop. The whole sentence when `subject` is null. */
  predicate: string;
  /** Whether the line reports a problem. Drives emphasis only — never content. */
  tone: 'risk' | 'steady' | 'unmeasured';
}

const pct = (n: number) => `${Math.round(n)}%`;

export function paceVerdict(sprint: SprintItem | null): PaceVerdict {
  if (!sprint) {
    return { subject: null, predicate: 'No sprint is running on this board.', tone: 'unmeasured' };
  }

  const p = sprintPace(sprint);
  const verdict = (predicate: string, tone: PaceVerdict['tone']): PaceVerdict => ({
    subject: sprint.name,
    predicate,
    tone,
  });

  // An overrun is a calendar fact and outranks every measurement below it: the
  // end date has passed and the sprint is still open, whether or not anyone
  // estimated anything.
  if (p.isOverrun && p.daysLeft !== null) {
    const over = -p.daysLeft;
    const ago = `it ended ${over} day${over === 1 ? '' : 's'} ago and is still open`;
    return verdict(
      p.donePct === null
        ? `overran — ${ago}.`
        : `overran — ${ago}, with ${pct(p.donePct)} of points done.`,
      'risk',
    );
  }

  if (p.donePct === null) {
    return verdict(
      p.dayOf !== null && p.windowDays !== null
        ? `has no point estimates, so pace cannot be measured — day ${p.dayOf} of ${p.windowDays}.`
        : 'has no point estimates and no dates, so pace cannot be measured.',
      'unmeasured',
    );
  }

  if (p.elapsedPct === null) {
    return verdict(
      `has no dates set, so pace cannot be measured — ${pct(p.donePct)} of points done.`,
      'unmeasured',
    );
  }

  // The rule's own grace period, said out loud rather than silently rendered as
  // a pass. A sprint two days in with nothing finished has started, not failed.
  if (p.elapsedPct < GRACE_ELAPSED_PCT) {
    return verdict(
      `has just started — day ${p.dayOf} of ${p.windowDays}, ${pct(p.donePct)} of points done. Pace is not judged in the first quarter of a sprint.`,
      'unmeasured',
    );
  }

  if (p.isBehind) {
    return verdict(
      `is behind pace — ${pct(p.donePct)} of points done against ${pct(p.elapsedPct)} of the window elapsed.`,
      'risk',
    );
  }

  return verdict(
    `is on pace — ${pct(p.donePct)} of points done, ${pct(p.elapsedPct)} of the window elapsed.`,
    'steady',
  );
}

/**
 * Riskiest first, then soonest to end.
 *
 * The order is the argument: whoever opens this page should find the sprint
 * that will not land at the top, without sorting, filtering or reading nine
 * rows first. Ties break on how far behind the pace a sprint is, so two behind
 * sprints are not ordered by accident.
 */
export function byRiskThenEndDate(
  a: { pace: SprintPace; endDate: string | null; boardName: string },
  b: { pace: SprintPace; endDate: string | null; boardName: string },
): number {
  if (a.pace.isOverrun !== b.pace.isOverrun) return a.pace.isOverrun ? -1 : 1;
  if (a.pace.isBehind !== b.pace.isBehind) return a.pace.isBehind ? -1 : 1;

  const gap = (p: SprintPace) =>
    p.donePct !== null && p.elapsedPct !== null ? p.donePct - p.elapsedPct : Number.POSITIVE_INFINITY;
  const gapDiff = gap(a.pace) - gap(b.pace);
  if (gapDiff !== 0) return gapDiff;

  // A sprint with no end date sorts last rather than first: an unknown deadline
  // is not an urgent one.
  if (a.endDate !== b.endDate) {
    if (a.endDate === null) return 1;
    if (b.endDate === null) return -1;
    return a.endDate < b.endDate ? -1 : 1;
  }
  return a.boardName.localeCompare(b.boardName);
}
